'use strict';

import Cropper from 'cropperjs';
import 'cropperjs/dist/cropper.min.css';

/**
 * P4.3.2 — Banner-Cropper mit zwei Modi:
 *   1) Re-Crop: bestehendes Original-Bild ist schon im DOM (Form .js-banner-recrop-form,
 *      <img id="bannerRecropSource">), Cropper wird sofort beim Page-Load mounted.
 *   2) Upload: User waehlt eine neue Datei, FileReader fuettert <img id="bannerCropperSource">,
 *      Cropper wird nach dem Load mounted.
 *
 * Beide schreiben Crop-Koordinaten (in Original-Pixeln) in die Hidden-Inputs ihres Forms,
 * Backend cropt + skaliert auf 1500x500.
 */
export default class BannerCropper {

    initialize() {
        this.page = document.querySelector('.js-banner-page');
        if (!this.page) {
            return;
        }

        this.recropForm = this.page.querySelector('.js-banner-recrop-form');
        this.uploadForm = this.page.querySelector('.js-banner-upload-form');

        if (this.recropForm) {
            this.setupRecrop();
        }
        if (this.uploadForm) {
            this.setupUpload();
        }
    }

    // ===== Re-Crop des bestehenden Originals =====

    setupRecrop() {
        const sourceImg = this.recropForm.querySelector('#bannerRecropSource');
        if (!sourceImg) {
            return;
        }

        // Cropper darf nicht in einem collapsed (display:none) Container mounted werden —
        // er braucht echte Layout-Dimensionen. Wir warten auf 'shown.bs.collapse'.
        const collapseEl = this.recropForm.closest('.collapse');
        const mountNow = () => {
            if (this.recropCropper) { return; } // schon mounted
            const doMount = () => {
                this.recropCropper = this.mountCropper(this.recropForm, sourceImg);
            };
            if (sourceImg.complete && sourceImg.naturalWidth > 0) {
                doMount();
            } else {
                sourceImg.addEventListener('load', doMount, { once: true });
            }
        };
        if (collapseEl) {
            // Wenn collapse bereits offen (z.B. nach Form-Submit re-show): direkt mounten
            if (collapseEl.classList.contains('show')) {
                mountNow();
            }
            collapseEl.addEventListener('shown.bs.collapse', mountNow);
            collapseEl.addEventListener('hidden.bs.collapse', () => {
                // Cropper destroy bei close, damit es beim re-open frisch initialisiert wird
                if (this.recropCropper) {
                    this.recropCropper.destroy();
                    this.recropCropper = null;
                }
            });
        } else {
            mountNow();
        }

        this.bindCropperButtons(this.recropForm, () => this.recropCropper);
        this.recropForm.addEventListener('submit', (e) => this.onSubmit(e, this.recropForm, () => this.recropCropper));
    }

    // ===== Neuer Upload mit Crop =====

    setupUpload() {
        const fileInput = this.uploadForm.querySelector('.js-banner-file');
        const sourceImg = this.uploadForm.querySelector('#bannerCropperSource');
        const wrap = this.uploadForm.querySelector('.js-banner-cropper-wrap');
        if (!fileInput || !sourceImg || !wrap) {
            return;
        }

        fileInput.addEventListener('change', (e) => this.onFileChange(e, sourceImg, wrap));
        this.bindCropperButtons(this.uploadForm, () => this.uploadCropper);
        this.uploadForm.addEventListener('submit', (e) => this.onSubmit(e, this.uploadForm, () => this.uploadCropper, fileInput));
    }

    onFileChange(e, sourceImg, wrap) {
        this.hideError(this.uploadForm);
        const file = e.target.files && e.target.files[0];
        if (!file) {
            this.destroyUploadCropper(wrap);
            return;
        }
        if (!['image/jpeg', 'image/png'].includes(file.type)) {
            this.showError(this.uploadForm, 'Bitte nur JPEG oder PNG hochladen.');
            e.target.value = '';
            return;
        }
        if (file.size > 15 * 1024 * 1024) {
            this.showError(this.uploadForm, 'Das Bild darf nicht größer als 15 MB sein.');
            e.target.value = '';
            return;
        }

        const reader = new FileReader();
        reader.onload = (ev) => {
            const dataUrl = ev.target.result;
            const probe = new Image();
            probe.onload = () => {
                if (probe.naturalWidth < 600 || probe.naturalHeight < 200) {
                    this.showError(this.uploadForm,
                        `Das Bild ist zu klein (${probe.naturalWidth}×${probe.naturalHeight}). ` +
                        `Mindestens 600×200 ist nötig.`
                    );
                    e.target.value = '';
                    this.destroyUploadCropper(wrap);
                    return;
                }
                sourceImg.src = dataUrl;
                wrap.style.display = '';
                // mountCropper braucht das geladene img — eines load-Tick warten:
                sourceImg.addEventListener('load', () => {
                    this.uploadCropper = this.mountCropper(this.uploadForm, sourceImg);
                }, { once: true });
            };
            probe.onerror = () => {
                this.showError(this.uploadForm, 'Bild konnte nicht gelesen werden.');
                e.target.value = '';
            };
            probe.src = dataUrl;
        };
        reader.onerror = () => {
            this.showError(this.uploadForm, 'Datei konnte nicht gelesen werden.');
            e.target.value = '';
        };
        reader.readAsDataURL(file);
    }

    destroyUploadCropper(wrap) {
        if (this.uploadCropper) {
            this.uploadCropper.destroy();
            this.uploadCropper = null;
        }
        if (wrap) {
            wrap.style.display = 'none';
        }
        this.clearCropInputs(this.uploadForm);
    }

    // ===== gemeinsam =====

    mountCropper(form, sourceImg) {
        const preview = form.querySelector('.js-banner-preview');
        const updateInputs = () => {
            if (!cropper) { return; }
            const data = cropper.getData(true);
            form.querySelector('.js-crop-x').value = Math.max(0, data.x);
            form.querySelector('.js-crop-y').value = Math.max(0, data.y);
            form.querySelector('.js-crop-w').value = Math.max(1, data.width);
            form.querySelector('.js-crop-h').value = Math.max(1, data.height);
        };
        const cropper = new Cropper(sourceImg, {
            aspectRatio: 3 / 1,
            viewMode: 1,
            autoCropArea: 1,
            background: false,
            modal: true,
            responsive: true,
            checkOrientation: true,
            preview: preview || undefined,
            ready: updateInputs,
            crop: updateInputs,
        });
        return cropper;
    }

    bindCropperButtons(form, getCropper) {
        form.querySelector('.js-cropper-zoom-in')?.addEventListener('click', () => getCropper()?.zoom(0.1));
        form.querySelector('.js-cropper-zoom-out')?.addEventListener('click', () => getCropper()?.zoom(-0.1));
        form.querySelector('.js-cropper-reset')?.addEventListener('click', () => getCropper()?.reset());
    }

    onSubmit(e, form, getCropper, requireFileInput = null) {
        const cropper = getCropper();
        // Beim Upload-Form: wenn keine Datei gewaehlt, blocken
        if (requireFileInput) {
            const hasFile = requireFileInput.files && requireFileInput.files[0];
            if (!hasFile) {
                e.preventDefault();
                this.showError(form, 'Bitte zuerst eine Datei wählen.');
                return;
            }
            if (!cropper) {
                e.preventDefault();
                this.showError(form, 'Bitte warten bis das Bild geladen ist.');
                return;
            }
        }
        if (cropper) {
            const data = cropper.getData(true);
            const w = Math.max(1, data.width);
            const h = Math.max(1, data.height);
            if (w < 600 || h < 200) {
                e.preventDefault();
                this.showError(form, `Crop-Ausschnitt zu klein (${w}×${h}). Mindestens 600×200 ist nötig.`);
                return;
            }
            form.querySelector('.js-crop-x').value = Math.max(0, data.x);
            form.querySelector('.js-crop-y').value = Math.max(0, data.y);
            form.querySelector('.js-crop-w').value = w;
            form.querySelector('.js-crop-h').value = h;
        }
    }

    clearCropInputs(form) {
        ['.js-crop-x', '.js-crop-y', '.js-crop-w', '.js-crop-h'].forEach(sel => {
            const el = form.querySelector(sel);
            if (el) { el.value = ''; }
        });
    }

    showError(form, msg) {
        const box = form.querySelector('.js-banner-cropper-error');
        if (box) {
            box.textContent = msg;
            box.style.display = '';
        }
    }

    hideError(form) {
        const box = form.querySelector('.js-banner-cropper-error');
        if (box) {
            box.textContent = '';
            box.style.display = 'none';
        }
    }
}
